import { toast } from "@/components/ui";
import { BodyText } from "@/components/ui/Text";
import { useCreateAllergy } from "@/features/Patient/MedicalRecords/MedicationsAllergies/network/useAllergies";
import { useState } from "react";
import { CiqAllergyMetadata } from "./CiqAllergyMetadata";
import { CiqAllergyPopover } from "./CiqAllergyPopover";
import { CiqAllergySearch } from "./CiqAllergySearch";
import { CiqAllergySingleRow } from "./CiqAllergySingleRow";
import type {
  CodifiedAllergen,
  ExtractedAllergy,
} from "./useAmazonVisitIntake";
import { useMarkAllergenAddedToChart } from "./useMarkAllergenAddedToChart";

interface CiqAllergyProps {
  allergy: ExtractedAllergy;
  patientId: string;
  encounterId?: string;
  questionnaireResponseId?: string;
}

export const CiqAllergy = ({
  allergy,
  patientId,
  encounterId,
  questionnaireResponseId,
}: CiqAllergyProps) => {
  const [wasAutoCharted] = useState(
    allergy.amazonRxcuiMatched === true &&
      Boolean(
        allergy.codifiedAllergies?.find(
          (a) =>
            a.allergenId === allergy.amazonRxcuiAllergenId && a.addedToChartAt,
        ),
      ),
  );
  const [addedAllergen, setAddedAllergen] = useState<
    CodifiedAllergen | undefined
  >(allergy.codifiedAllergies?.find((a) => a.addedToChartAt));

  const {
    mutateAsync: createAllergy,
    isPending,
    variables,
  } = useCreateAllergy({ patientId });
  const { mutateAsync: markAllergenAddedToChart } =
    useMarkAllergenAddedToChart();

  const pendingAllergenName = isPending ? variables?.allergyName : undefined;

  const codifiedAllergens = allergy.codifiedAllergies ?? [];
  const noCodedAllergens = codifiedAllergens.length === 0;
  const singleCodedAllergen =
    codifiedAllergens.length === 1 ? codifiedAllergens[0] : null;

  const displayName =
    allergy.name ?? codifiedAllergens[0]?.name ?? "Unknown allergy";

  const handleAdd = async (allergen: CodifiedAllergen) => {
    const allergenName = allergen.name ?? displayName;
    const allergenId = allergen.allergenId ?? "";
    const allergenSystem = allergen.system ?? "";

    if (!allergenId || !allergenSystem) {
      toast.error(`Failed to add ${allergenName} to allergies`);
      return;
    }

    const symptomDescription = allergy.symptoms
      .map((s) => s.symptom)
      .filter(Boolean)
      .join(", ");
    const reactions =
      allergy.severity || symptomDescription
        ? [
            {
              ...(allergy.severity ? { severity: allergy.severity } : {}),
              ...(symptomDescription
                ? { description: symptomDescription }
                : {}),
            },
          ]
        : undefined;

    try {
      await createAllergy({
        allergyCode: allergenId,
        allergyCodeSystem: allergenSystem,
        allergyName: allergenName,
        allergyType: allergy.type ?? "allergy",
        encounterId,
        reactions,
      });
      setAddedAllergen(allergen);
    } catch {
      // useCreateAllergy already surfaces a toast on error
      return;
    }

    if (questionnaireResponseId) {
      try {
        await markAllergenAddedToChart({
          questionnaireResponseId,
          allergenId,
        });
      } catch {}
    }
  };

  return (
    <div className="border-l-4 border-ai pl-2 py-0.5 rounded">
      <BodyText size="sm" bold>
        {displayName}
      </BodyText>
      <CiqAllergyMetadata
        type={allergy.type}
        severity={allergy.severity}
        symptoms={allergy.symptoms}
      />
      {singleCodedAllergen ? (
        <CiqAllergySingleRow
          allergen={singleCodedAllergen}
          onAdd={handleAdd}
          isPending={isPending}
          isAdded={addedAllergen?.allergenId === singleCodedAllergen.allergenId}
          addedLabel={wasAutoCharted ? "Auto-charted" : "Added"}
        />
      ) : noCodedAllergens ? (
        <CiqAllergySearch
          defaultSearchTerm={(allergy.name ?? "").split(" ")[0] ?? ""}
          onAdd={handleAdd}
          isPending={isPending}
          addedAllergen={addedAllergen}
          pendingAllergenName={pendingAllergenName}
          addedLabel={wasAutoCharted ? "Auto-charted" : "Added"}
        />
      ) : (
        <CiqAllergyPopover
          allergens={codifiedAllergens}
          onAdd={handleAdd}
          isPending={isPending}
          addedAllergen={addedAllergen}
          pendingAllergenName={pendingAllergenName}
          addedLabel={wasAutoCharted ? "Auto-charted" : "Added"}
        />
      )}
    </div>
  );
};
